
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BookOpen, 
  TrendingUp,
  Target,
  Sparkles
} from 'lucide-react';
import RecommendationCard from './RecommendationCard';
import { LearningRecommendationsProps } from './types';

const LearningRecommendations: React.FC<LearningRecommendationsProps> = ({
  completedNodes,
  inProgressNodes,
  viewedNodes,
  recentlyCompleted,
  allNodes,
  currentNodeId
}) => {
  // Filter out current node and already accessed nodes
  const availableNodes = allNodes.filter(node => 
    node.status === 'published' &&
    node.id !== currentNodeId &&
    !completedNodes.includes(node.id) &&
    !inProgressNodes.includes(node.id) &&
    !viewedNodes.includes(node.id)
  );

  // Find nodes from same discipline as recently completed
  const relatedByDiscipline = availableNodes.filter(node =>
    recentlyCompleted.some(progress => {
      const completedNode = allNodes.find(n => n.id === progress.node_id);
      return completedNode?.discipline_id === node.discipline_id;
    })
  ).slice(0, 3);

  // Find beginner-friendly nodes for new users
  const beginnerNodes = availableNodes
    .filter(node => node.node_type === 'lesson' || node.node_type === 'concept')
    .slice(0, 3);

  // Get next steps based on progress patterns
  const getNextSteps = () => {
    if (completedNodes.length === 0) {
      return {
        title: "Start Your Learning Journey",
        description: "Begin with these foundational concepts",
        nodes: beginnerNodes,
        icon: BookOpen,
        color: "text-blue-500"
      };
    } else if (completedNodes.length < 5) {
      return {
        title: "Build Your Foundation",
        description: "Continue exploring related topics",
        nodes: relatedByDiscipline,
        icon: TrendingUp,
        color: "text-green-500"
      };
    } else {
      return {
        title: "Deepen Your Knowledge",
        description: "Advanced topics in your areas of interest",
        nodes: relatedByDiscipline,
        icon: Target,
        color: "text-purple-500"
      };
    }
  };

  const nextSteps = getNextSteps();

  // Personalized learning path suggestions
  const getPersonalizedPath = () => {
    const disciplines = [...new Set(
      recentlyCompleted.map(progress => {
        const node = allNodes.find(n => n.id === progress.node_id);
        return node?.disciplines?.name;
      }).filter(Boolean)
    )];

    if (disciplines.length > 0) {
      return `Based on your recent progress in ${disciplines.join(', ')}, here are your next recommended steps.`;
    }
    
    return "Based on your learning patterns, here are personalized recommendations for you.";
  };

  if (nextSteps.nodes.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Sparkles className="h-8 w-8 mx-auto text-black mb-2" />
          <p className="text-black">
            Great job! You've explored most available content. Check back for new additions!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <nextSteps.icon className={`h-5 w-5 ${nextSteps.color}`} />
          {nextSteps.title}
        </CardTitle>
        <p className="text-sm text-black">
          {getPersonalizedPath()}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-black mb-4">
          {nextSteps.description}
        </p>
        
        <div className="grid gap-4">
          {nextSteps.nodes.map((node, index) => (
            <RecommendationCard
              key={node.id}
              node={node}
              index={index}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default LearningRecommendations;
